import Table, { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { useState } from 'react';
import { get, some } from 'lodash';
import Tooltip from 'antd/lib/tooltip';
import Alert from 'antd/lib/alert';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import WarningOutlined from '@ant-design/icons/WarningOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import FileTextOutlined from '@ant-design/icons/lib/icons/FileTextOutlined';
import { ERROR_COLOR, PRIMARY_COLOR, WARNING_COLOR } from 'src/theme';
import { toAllowedStringLength } from 'src/domain/common/formattersToAllowedValueLength';
import { useImportContext } from '../ImportProvider';
import { ParsedRecord } from '../ImportParser';
import './ImportPreview.css';

export function ImportPreview<T extends string>() {
    const { csvConfig, viewState, importErrors } = useImportContext();

    const [paginationData, setPaginationData] = useState<TablePaginationConfig>({ current: 1, pageSize: 10 });

    const getItemIndex = (rowIndex: number) => {
        const currentPage = paginationData?.current || 1;
        const itemsPerPage = paginationData?.pageSize || 10;
        return rowIndex + (currentPage - 1) * itemsPerPage;
    };

    const rowHasError = (rowIndex: number) => {
        const itemIndex = getItemIndex(rowIndex);
        return Object.values(viewState.records?.[itemIndex]).some(item => item.error);
    };

    const rowHasWarning = (rowIndex: number) => {
        const itemIndex = getItemIndex(rowIndex);
        return Object.values(viewState.records?.[itemIndex]).some(item => item.warning);
    };

    const colHasError = (rowIndex: number, colIndex: number) => {
        const itemIndex = getItemIndex(rowIndex);
        return Object.values(viewState.records?.[itemIndex])?.[colIndex]?.error;
    };

    const colHasWarning = (rowIndex: number, colIndex: number) => {
        const itemIndex = getItemIndex(rowIndex);
        return Object.values(viewState.records?.[itemIndex])?.[colIndex]?.warning;
    };

    const getRowClassName = (index: number): string => {
        if (rowHasError(index)) {
            return 'row-with-error';
        }

        if (rowHasWarning(index)) {
            return 'row-with-warning';
        }

        return '';
    };

    const getColClassName = (rowIndex: number, colIndex: number): string => {
        if (colHasError(rowIndex, colIndex)) {
            return 'col-with-error';
        }

        if (colHasWarning(rowIndex, colIndex)) {
            return 'col-with-warning';
        }

        return '';
    };

    function makeRowKeyClosure(prefix: string) {
        let rowIndex = 0;
        return () => `${prefix}-${rowIndex++}`;
    }

    const getAllRowErrors = (rowIndex: number) => {
        const itemIndex = getItemIndex(rowIndex);
        const colsWithErrors = Object.values(get(viewState.records, itemIndex, [])).filter(item => !!item.error);
        const rowErrors = colsWithErrors.map(item => item.error);

        const importError = importErrors[itemIndex];
        if (importErrors[itemIndex]) {
            return rowErrors.concat(importError);
        }

        return rowErrors;
    };

    const getAllRowWarnings = (rowIndex: number) => {
        const itemIndex = getItemIndex(rowIndex);
        const colsWithWarnings = Object.values(get(viewState.records, itemIndex, [])).filter(item => !!item.warning);
        return colsWithWarnings.map(item => item.warning);
    };

    const buildColumns = (): ColumnsType<any> => {
        const columns: ColumnsType<any> = [];

        csvConfig.forEach((col, colIndex) => {
            columns.push({
                title: col.column,
                dataIndex: [colIndex],
                width: col.width === undefined ? 100 : col.width,
                ellipsis: true,
                render: (text: any, record: ParsedRecord<T>, index: number) => {
                    return <div className={getColClassName(index, colIndex)}>{record[col.column as T]?.value || ''}</div>;
                },
            });
        });

        columns.unshift(
            // Add Import Status column
            {
                title: 'Status',
                dataIndex: 'status',
                width: 60,
                align: 'center',
                render: (text: number, record: any, index: number) => {
                    const rowErrors = getAllRowErrors(index);
                    const rowWarnings = getAllRowWarnings(index);

                    if (rowErrors.length) {
                        return (
                            <Tooltip title="Please, check 'Errors' column">
                                <CloseOutlined style={{ color: ERROR_COLOR }} />
                            </Tooltip>
                        );
                    }

                    if (rowWarnings.length) {
                        return (
                            <Tooltip title="Please, check 'Warnings' column">
                                <WarningOutlined style={{ color: WARNING_COLOR }} />
                            </Tooltip>
                        );
                    }

                    return <CheckOutlined style={{ color: PRIMARY_COLOR }} />;
                },
            },

            // Add Import Errors column
            {
                title: 'Errors',
                dataIndex: 'errors',
                width: 200,
                render: (text: number, record: any, index: number) => {
                    const rowErrors = getAllRowErrors(index) || [];

                    if (rowErrors.length === 0) {
                        return '';
                    }

                    const errorsMessage = rowErrors.join(' | ');

                    return <Tooltip title={errorsMessage}>{toAllowedStringLength(errorsMessage, 50)}</Tooltip>;
                },
            },
            ...(some(viewState.records, row => some(row, item => !!item.warning))
                ? [
                      {
                          title: 'Warnings',
                          dataIndex: 'warnings',
                          width: 200,
                          render: (text: number, record: any, index: number) => {
                              const rowWarnings = getAllRowWarnings(index) || [];

                              if (rowWarnings.length === 0) {
                                  return '';
                              }

                              const warningMessage = rowWarnings.join(' | ');

                              return <Tooltip title={warningMessage}>{toAllowedStringLength(warningMessage, 50)}</Tooltip>;
                          },
                      },
                  ]
                : [])
        );

        return columns;
    };

    return (
        <div className="import-preview">
            <ImportPreviewFileSummary />
            <p></p>
            <Table
                bordered
                sticky
                size="small"
                rowKey={makeRowKeyClosure('import-preview-row')}
                rowClassName={(row, index) => getRowClassName(index)}
                scroll={{ x: 'scroll' }}
                columns={buildColumns()}
                dataSource={viewState.records}
                onChange={pagination => setPaginationData(pagination)}
            />
        </div>
    );
}

function ImportPreviewFileSummary() {
    const { viewState, importErrors, importHasErrors } = useImportContext();
    const recordsCount = viewState.records.length;

    /** these are front validation errors only */
    const errorsCount = viewState.records.filter(row => some(row, item => !!item.error)).length;

    /** here we sum front validation errors and import (from backend) errors */
    const allErrors = errorsCount + importErrors.filter(Boolean).length;

    const filename = viewState.file?.name || null;

    if (importHasErrors) {
        return (
            <Alert
                showIcon
                icon={<FileTextOutlined color="red" />}
                style={{ backgroundColor: 'white', border: 'none' }}
                type="error"
                message={filename ? `File "${filename}" contains errors` : 'Uploaded file contains errors'}
                description={
                    allErrors === 0
                        ? `File has ${recordsCount} ${recordsCount === 1 ? 'record' : 'records'}. ${allErrors} records with errors.`
                        : `File has ${recordsCount} ${recordsCount === 1 ? 'record' : 'records'}. ${allErrors} ${allErrors === 1 ? 'record' : 'records'} with errors!`
                }
            />
        );
    }

    return (
        <Alert
            showIcon
            icon={<FileTextOutlined />}
            style={{ backgroundColor: 'white', border: 'none' }}
            type="info"
            message={filename ? `File "${filename}"` : 'Uploaded file'}
            description={
                recordsCount > 0
                    ? `File has ${recordsCount} ${recordsCount === 1 ? 'record' : 'records'}. ${recordsCount === 1 ? 'It is' : 'All are'} ready to be imported.`
                    : 'File has 0 records'
            }
        />
    );
}
